/**
 * StatusSelector — Selector de 3 estados para noticias y eventos.
 *
 * Estados:
 *   • draft        — borrador, solo visible en admin
 *   • private_link — accesible solo vía URL con token (compartible)
 *   • public       — visible en el sitio público
 *
 * Cuando el estado es "private_link", muestra el enlace privado con un botón
 * para copiarlo al portapapeles.
 */
import React, { useState } from 'react';
import { FileText, Link as LinkIcon, Globe, Copy, Check, ExternalLink } from 'lucide-react';

const STATUSES = [
  {
    value: 'draft',
    label: 'Borrador',
    description: 'Solo visible en el panel admin. No aparece en el sitio.',
    icon: FileText,
    activeClasses: 'border-gray-400 bg-gray-50 shadow-sm',
    iconActiveClass: 'text-gray-700',
    checkClass: 'bg-gray-600',
  },
  {
    value: 'private_link',
    label: 'Link privado',
    description: 'Accesible solo con el enlace privado. Útil para compartir con consejeros antes de publicar.',
    icon: LinkIcon,
    activeClasses: 'border-amber-400 bg-amber-50 shadow-sm',
    iconActiveClass: 'text-amber-600',
    checkClass: 'bg-amber-500',
  },
  {
    value: 'public',
    label: 'Público',
    description: 'Visible en el sitio público para todos los visitantes.',
    icon: Globe,
    activeClasses: 'border-emerald-400 bg-emerald-50 shadow-sm',
    iconActiveClass: 'text-emerald-600',
    checkClass: 'bg-emerald-500',
  },
];

export default function StatusSelector({ value, onChange, previewToken, previewPath }) {
  const [copied, setCopied] = useState(false);

  const privateUrl = previewToken
    ? `${window.location.origin}${previewPath}/${previewToken}`
    : null;

  async function copy() {
    if (!privateUrl) return;
    try {
      await navigator.clipboard.writeText(privateUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // Fallback for older browsers
      const ta = document.createElement('textarea');
      ta.value = privateUrl;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  }

  return (
    <div data-testid="status-selector">
      <label className="block text-xs font-medium text-charcoal-600 mb-2 uppercase tracking-wider">
        Estado de publicación
      </label>
      <div className="grid sm:grid-cols-3 gap-2">
        {STATUSES.map((s) => {
          const Icon = s.icon;
          const active = value === s.value;
          return (
            <button
              key={s.value}
              type="button"
              onClick={() => onChange(s.value)}
              data-testid={`status-option-${s.value}`}
              className={`relative text-left p-3 rounded-xl border-2 transition-all ${
                active
                  ? s.activeClasses
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2 mb-1">
                <Icon size={14} className={active ? s.iconActiveClass : 'text-charcoal-400'} />
                <span className={`text-sm font-semibold ${active ? 'text-charcoal-800' : 'text-charcoal-600'}`}>
                  {s.label}
                </span>
                {active && (
                  <span className={`ml-auto inline-flex items-center justify-center w-4 h-4 rounded-full text-white ${s.checkClass}`}>
                    <Check size={10} />
                  </span>
                )}
              </div>
              <p className="text-[11px] text-charcoal-500 leading-relaxed">{s.description}</p>
            </button>
          );
        })}
      </div>

      {/* Private link panel — shown for private_link AND draft (draft items still
          have a token so admins can share a preview with reviewers). */}
      {(value === 'private_link' || value === 'draft') && privateUrl && (
        <div className="mt-3 bg-amber-50 border border-amber-200 rounded-xl p-3" data-testid="status-private-link-panel">
          <div className="flex items-center gap-2 text-xs font-semibold text-amber-800 mb-2">
            <LinkIcon size={12} />
            {value === 'private_link' ? 'Enlace privado para compartir' : 'Enlace de vista previa (solo borrador)'}
          </div>
          <div className="flex items-center gap-2">
            <input
              readOnly
              value={privateUrl}
              data-testid="status-private-link-input"
              onClick={(e) => e.target.select()}
              className="flex-1 text-xs bg-white border border-amber-200 rounded-lg px-3 py-2 font-mono text-charcoal-700 select-all min-w-0"
            />
            <button
              type="button"
              onClick={copy}
              data-testid="status-copy-link-btn"
              className="inline-flex items-center gap-1.5 text-xs font-semibold bg-amber-500 text-white px-3 py-2 rounded-lg hover:bg-amber-600 whitespace-nowrap"
            >
              {copied ? <><Check size={12} /> Copiado</> : <><Copy size={12} /> Copiar</>}
            </button>
            <a
              href={privateUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="status-open-link-btn"
              className="inline-flex items-center gap-1.5 text-xs font-semibold bg-white border border-amber-300 text-amber-700 px-3 py-2 rounded-lg hover:bg-amber-100"
            >
              <ExternalLink size={12} /> Abrir
            </a>
          </div>
          <p className="text-[11px] text-amber-700 mt-2 leading-relaxed">
            {value === 'private_link'
              ? 'Cualquier persona con este enlace puede ver el contenido sin iniciar sesión.'
              : 'Este enlace funciona solo para vista previa interna. No se mostrará en el sitio hasta que cambies el estado a "Público".'}
          </p>
        </div>
      )}
    </div>
  );
}
